import { useCallback, useEffect, useState } from 'react';
import api from './useApi';
import useRefreshOnFocus from './useRefreshOnFocus';

// How many open actions are on the current user, for the My Actions badge in
// the sidebar.
//
// This runs for the whole session on every page, so it reads the count-only
// endpoint rather than the queue: the queue returns up to 300 enriched rows
// with per-action message counts, which is a lot of work to render one number.
// Polled on the same 30s cadence as the notification bell, and caught up
// whenever the tab is returned to, so completing an action in another tab (or
// somebody raising one on you) shows up without a reload.
const POLL_INTERVAL = 30000;

// `path` points it at another count-only endpoint of the same { open, overdue }
// shape: the SLT Register badge reads slt-register/mine/count.
export default function useOpenActionsCount({ enabled = true, path = 'actions/mine/count' } = {}) {
  const [counts, setCounts] = useState({ open: 0, overdue: 0 });

  const refresh = useCallback(() => {
    if (!enabled) return;
    api.get(path)
      .then(c => setCounts({ open: Number(c?.open || 0), overdue: Number(c?.overdue || 0) }))
      // Non-fatal: a failed poll leaves the last known count rather than
      // blanking the badge, and the next one will correct it.
      .catch(() => {});
  }, [enabled, path]);

  useEffect(() => {
    if (!enabled) return undefined;
    refresh();
    const timer = setInterval(refresh, POLL_INTERVAL);
    return () => clearInterval(timer);
  }, [enabled, refresh]);

  useRefreshOnFocus(refresh);

  return { ...counts, refresh };
}
